<template>
  <div
    :class="[
      `VisionTextInput--shape-${shape}`,
      `VisionTextInput--cursor-${cursor}`,
      {
        'VisionTextInput--disabled': disabled,
      }
    ]"
  >
    <div
      v-if="label || labelHint"
      class="VisionTextInput__label-wrapper"
    >
      <label
        :for="id"
        class="VisionTextInput__label"
      >{{
        label
      }}</label>
      <span
        v-if="labelHint"
        class="VisionTextInput__label-hint"
      >{{
        labelHint
      }}</span>
    </div>
    <div class="VisionTextInput__input-wrapper">
      <input
        :id="id"
        :type="type"
        :name="name"
        class="VisionTextInput__input"
        :class="[
          error
            ? 'VisionTextInput__input--error'
            : ''
        ]"
        :placeholder="placeholder"
        :aria-invalid="error != ''"
        :aria-describedby="ariaDescribedBy"
        :value="value"
        :disabled="disabled"
        :autocomplete="autocomplete"
        v-on="{
          keydown: handleKeyDown,
          input: handleInput,
          focus: handleFocus,
          blur: handleBlur,
        }"
        @keyup.enter="handleEnter"
        @keyup.escape="handleEscape"
      />
      <div
        v-if="error || icon"
        class="VisionTextInput__icon"
        :class="{ 'VisionTextInput__icon--error': error }"
        @click="handleFocus()"
      >
        <VisionIcon
          :icon="error ? 'exclamation' : icon"
          :colour="error ? 'red' : 'black'"
          :rotate="error ? 0 : iconRotation"
          :size="error ? 'small' : 'xx-small'"
        />
      </div>
    </div>
    <p
      v-if="error"
      :id="ariaDescribedBy"
      class="VisionTextInput__error-msg"
    >
      {{ error }}
    </p>
    <p
      v-if="hint && !error"
      :id="ariaDescribedBy"
      class="VisionTextInput__hint"
    >
      {{ hint }}
    </p>
  </div>
</template>
<script>
import VisionIcon from '../VisionIcon/VisionIcon.vue'
export default {
  name: 'VisionTextInput',

  components: {
    VisionIcon
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    type: {
      type: String,
      default: "text",
      validator(value) {
        return ["text", "email", "select-search", "tel"].includes(value);
      },
    },
    error: {
      type: String,
      default: "",
      required: false,
    },
    hint: {
      type: String,
      default: "",
      required: false,
    },
    labelHint: {
      type: String,
      default: "",
      required: false,
    },
    value: {
      type: String,
      default: "",
      required: false,
    },
    autocomplete: {
      type: String,
      default: 'on',
      required: false
    },
    integerOnly: {
      type: Boolean,
      default: false
    },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return [
          'rounded',
          'square'
        ].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    cursor: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'pointer'].includes(value)
      }
    },
    icon: {
      type: String,
      default: '',
      required: false,
      validator(value) {
        return ['', 'chevron'].includes(value)
      },
    },
    iconRotation: {
      type: Number,
      default: 0,
      required: false
    },
  },
  
  emits: ['update:value', 'keydown', 'focus', 'blur', 'enter', 'escape'],

  computed: {
    ariaDescribedBy() {
      if (this.error) {
        return `${this.id}-error`;
      } else if (this.hint) {
        return `${this.id}-description`;
      }

      return "";
    },
  },

  methods: {
    handleInput (event) {
      let value = event.target.value

      if (this.type === 'number' && this.integerOnly) value = Math.round(value)

      this.$emit('update:value', value)
    },
    handleKeyDown (event) {
      if (!this.type === 'number') return

      // Prevent dot (.) if we're only accepting integer values
      if (event.keyCode === 110 || event.keyCode === 190) {
        if (this.integerOnly) {
          event.preventDefault()
        }
      }

      this.$emit('keydown', event)
    },
    handleFocus() {
      this.$emit('focus')
    },
    handleBlur() {
      this.$emit('blur')
    },
    handleEnter() {
      this.$emit('enter')
    },
    handleEscape() {
      this.$emit('escape')
    }
  }
};
</script>

<style lang="postcss">
.VisionTextInput__label-wrapper {
  @apply flex justify-between mb-1;
}

.VisionTextInput__label {
  @apply block text-sm font-medium text-off-black;
}

.VisionTextInput__label-hint {
  @apply text-xxs text-gray-500;
}

.VisionTextInput__input-wrapper {
  @apply flex relative;
}

.VisionTextInput__input {
  @apply flex-1 block
    w-full
    bg-grey-light
    p-xxs pr-10
    border-[1px]
    border-grey-light
    focus:outline-none
    focus-within:border-grey
    sm:text-sm;

  &[disabled] {
    @apply opacity-50;
  }
}

.VisionTextInput__input--error {
  @apply border-red text-red placeholder-red focus:ring-red focus:border-red;

  &::placeholder {
    @apply opacity-75;
  }
}

.VisionTextInput__icon {
  @apply absolute inset-y-0 right-0 pr-3 flex items-center;
}

.VisionTextInput__icon--error {
  @apply opacity-50 pointer-events-none;
}

.VisionTextInput__error-msg {
  @apply text-red;
}

.VisionTextInput__hint {
  @apply text-gray-500;
}

.VisionTextInput__error-msg,
.VisionTextInput__hint {
  @apply mt-2 text-xxs
}

/* SHAPES */

.VisionTextInput--shape-rounded {
  .VisionTextInput__label-wrapper,
  .VisionTextInput__error-msg,
  .VisionTextInput__hint {
    @apply px-xs;
  }

  .VisionTextInput__input {
    @apply rounded-full p-xs pr-12;
  }

  .VisionTextInput__icon {
    @apply pr-6;
  }
}

.VisionTextInput--shape-square {
  .VisionTextInput__input {
    @apply rounded-md;
  }
}

/* CURSORS */
.VisionTextInput--cursor-pointer {
  .VisionTextInput__input,
  .VisionTextInput__icon {
    @apply cursor-pointer;
  }
}

/* STATES */
.VisionTextInput--disabled {
  .VisionTextInput__label-wrapper,
  .VisionTextInput__hint {
    @apply opacity-40;
  }
}
</style>
